import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { WarrantyListResult } from '@econtractor/types';
import * as api from 'api/apiWarranties';
import { useToast } from 'context/ToastContext';

const useObjectWarranties = (objectId: number, eligible = false, onSaved?: () => void, enabled = true) => {
    const { t } = useTranslation('Warranties');
    const { showToast } = useToast();
    const feedback = useRef({ t, showToast });
    feedback.current = { t, showToast };
    const [result, setResult] = useState<WarrantyListResult | null>(null);
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(1);
    const [search, setSearch] = useState('');
    const [selected, setSelected] = useState<number[]>([]);
    const [revision, setRevision] = useState(0);
    const [cardId, setCardId] = useState<number | null>(null);
    const [showAttach, setShowAttach] = useState(false);
    const refresh = useCallback(() => {
        setRevision((r) => r + 1);
        onSaved?.();
    }, [onSaved]);
    useEffect(() => {
        if (!enabled) {
            setSelected([]);
            setLoading(false);
            return;
        }
        let cancelled = false;
        setLoading(true);
        void api
            .list({ page, onPage: 25, search }, objectId, eligible)
            .then((data) => {
                if (!cancelled) setResult(data);
            })
            .catch((error) => {
                if (!cancelled) {
                    feedback.current.showToast(
                        feedback.current.t(`errors.${api.errorCode(error)}`),
                        'danger',
                    );
                }
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [objectId, eligible, page, search, revision, enabled]);
    const mutate = async (ids: number[], detach = false): Promise<boolean> => {
        setLoading(true);
        try {
            await api.assign(objectId, ids, detach);
            setSelected([]);
            refresh();
            return true;
        } catch (error) {
            feedback.current.showToast(
                feedback.current.t(`errors.${api.errorCode(error)}`),
                'danger',
            );
            refresh();
            return false;
        } finally {
            setLoading(false);
        }
    };
    const toggle = (id: number) =>
        setSelected((prev) =>
            prev.includes(id) ? prev.filter((value) => value !== id) : [...prev, id],
        );
    return {
        result,
        loading,
        page,
        setPage,
        search,
        setSearch: (text: string) => {
            setSearch(text);
            setPage(1);
            setSelected([]);
        },
        selected,
        toggle,
        mutate,
        refresh,
        cardId,
        setCardId,
        showAttach,
        setShowAttach,
    };
};

export default useObjectWarranties;
